import React, { useCallback, useMemo, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { Box, Button, Grid, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import PropTypes from "prop-types";

import BreadCrumb from "Components/Common/BreadCrumb";
import BaseTable from "Components/Common/BaseTable";
import Tooltip from "Components/Common/Tooltip";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import SelectField from "Components/Common/SelectField";
import { getOptionLabel } from "common/enums";
import UserManagementForm from "./ApproverManagementForm";

import { USER_MANAGEMENT } from "tagTypes";

import { useDeleteUserApproverMutation } from "apis/User";

const RECORDS_PER_PAGE = 10;

const approverTypeOptions = [
  { label: "All", value: "" },
  { label: "Technical Sanction", value: true },
  { label: "Demand Estimate", value: false },
];

const UserManagement = ({ t }) => {
  const [deleteApproverRole, { isLoading: isApproverDeleting }] =
    useDeleteUserApproverMutation();

  const resetPageRef = useRef();

  const [query, setQuery] = useState({ isTechnicalSanction: "" });
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [isModelOpen, setIsModalOpen] = useState(false);
  const [userId, setUserId] = useState("");
  const [deleteUserId, setDeleteUserId] = useState("");

  const columns = useMemo(
    () => [
      {
        title: "Name",
        field: "user.name",
      },
      {
        title: "Role",
        field: "role.name",
      },
      {
        title: "Utility",
        renderCell: ({ utilityId }) =>
          utilityId
            .map((val) => getOptionLabel("utilityOptions", val))
            .join(", "),
      },
      {
        title: "Minimum Amount",
        field: "minimumAmount",
      },
      {
        title: "Maximum Amount",
        field: "maximumAmount",
      },
      {
        title: "Load Type",
        renderCell: ({ loadType }) =>
          getOptionLabel("powerRequirementOptions", loadType),
      },
      {
        title: "Premise Type",
        field: "premiseType.name",
      },
      {
        title: "Connection Type",
        renderCell: ({ isTemporaryConnection }) => {
          if (isTemporaryConnection !== null) {
            return isTemporaryConnection
              ? "Temporary Connection"
              : "Permanent Connection";
          }
          return "--";
        },
      },
      {
        title: "Approver Of",
        renderCell: ({ isTechnicalSanction }) =>
          isTechnicalSanction ? "Technical Sanction" : "Demand Estimate",
      },

      {
        title: "Actions",
        renderCell: ({ id }) => (
          <Box sx={{ minWidth: "100px" }}>
            <Tooltip title={t("Edit user")}>
              <IconButton
                onClick={() => {
                  setUserId(id);
                  setIsModalOpen(true);
                }}
              >
                <EditIcon color="primary" fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("Delete user")}>
              <IconButton onClick={() => setDeleteUserId(id)}>
                <DeleteIcon color="primary" fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    []
  );

  const serverSidePaginationConfig = useMemo(
    () => ({
      endpoint: "/digital-signature-approver",
      tags: [USER_MANAGEMENT],
      query,
    }),
    [query]
  );

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  return (
    <div className="page-content h-100">
      {isModelOpen && (
        <UserManagementForm
          id={userId}
          handleClose={() => {
            setIsModalOpen(false);
            setUserId("");
          }}
        />
      )}

      <ConfirmationDialog
        isLoading={isApproverDeleting}
        isOpen={!!deleteUserId}
        onCancel={() => setDeleteUserId("")}
        onConfirm={async () => {
          try {
            await deleteApproverRole(deleteUserId);
            setDeleteUserId("");
          } catch (error) {}
        }}
        message="Are you sure you want to delete this approver?"
        variant="warning"
        title="Warning"
      />
      <BreadCrumb title={t("Approver Management")} pageTitle={t("User")} />

      <Grid container className="d-flex justify-content-between">
        <Grid item xs={12} sm={6} md={4}>
          <SelectField
            label={t("Approver Type") + "*"}
            options={approverTypeOptions}
            onChange={(_, { value }) => {
              setQuery({ isTechnicalSanction: value });
              handleResetPage();
            }}
            value={approverTypeOptions.find(
              ({ value }) => value === query.isTechnicalSanction
            )}
          />
        </Grid>
        <Button
          className="mb-1"
          sx={{ maxHeight: "40px" }}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsModalOpen(true)}
        >
          {t("Add Approver")}
        </Button>
      </Grid>

      <BaseTable
        serverSidePaginationConfig={serverSidePaginationConfig}
        paginationType="serverSide"
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        columns={columns}
        pageSize={RECORDS_PER_PAGE}
        maxHeight="calc(100vh - 250px)"
      />
    </div>
  );
};

export default withTranslation()(UserManagement);

UserManagement.propTypes = {
  t: PropTypes.func,
};
